import { Box, Chip, Typography } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { WaitlistedClassItem } from "../../hooks/useWaitlistedClasses";

interface Props {
  learnerName: string;
  leaderName: string;
  availableSpaces: number;
  activity: Activity;
  classTime: string;
}

const WaitlistedClassItemInfo = ({
  learnerName,
  leaderName,
  availableSpaces,
  activity,
  classTime,
}: Props) => {
  const { getClassType } = useGetClassType();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Box
        flex
        sx={{
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 8,
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "0 1.1rem",
          }}
        >
          <AvailableSpacesChip availableSpaces={availableSpaces} />
          <Typography variant="subtitle2">{learnerName}</Typography>
        </Box>
        <Box
          flex
          sx={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "0 1.1rem",
          }}
        >
          <Typography variant="subtitle2">{`Taught by ${leaderName}`}</Typography>
          <GrayDot />
          <Typography variant="subtitle2">{getClassType(activity)}</Typography>
        </Box>
        <Typography variant="subtitle2">{classTime}</Typography>
      </Box>
    );
  }

  return (
    <Box
      flex
      sx={{
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "baseline",
        gap: "0 1.5rem",
      }}
    >
      <AvailableSpacesChip availableSpaces={availableSpaces} />
      <Typography variant="subtitle2">{learnerName}</Typography>
      <GrayDot />
      <Typography variant="subtitle2">{`Taught by ${leaderName}`}</Typography>
      <GrayDot />
      <Typography variant="subtitle2">{getClassType(activity)}</Typography>
    </Box>
  );
};

const GrayDot = () => {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "grey.200",
      }}
    />
  );
};

const AvailableSpacesChip = ({
  availableSpaces,
}: Pick<WaitlistedClassItem, "availableSpaces">) => {
  const { t } = useTranslation("client\\components\\WaitlistedClassList");
  return (
    <Chip
      color={availableSpaces ? "success" : "neutral"}
      size="small"
      label={
        availableSpaces
          ? t("{{availableSpaces}} available seat", {
              availableSpaces,
              count: availableSpaces,
              defaultValue_plural: "{{availableSpaces}} available seats",
            })
          : t("No available seats")
      }
    />
  );
};

const useGetClassType = () => {
  const { t } = useTranslation("client\\components\\WaitlistedClassList");

  const getClassType = (activity: Activity) => {
    if (activity.isSelfPaced) {
      return t("Self paced");
    }
    if (activity.hasTeacherSchedule) {
      return t("Live 1-on-1");
    }
    if (activity.isOneTime) {
      return t("Live one-time");
    }
    if (activity.size_max == 1) {
      return t("Live private");
    }
    return t("Live group");
  };

  return { getClassType };
};

export default WaitlistedClassItemInfo;
